import * as React from 'react';

import { enumRoleSortOption } from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  Col,
  FloatingToolbar,
  IconButton,
  Pagination,
  Row,
  SearchField,
  TinyTable,
  type PaginationProps,
} from '@rematter/ui-web';
import { useAtom, useSetAtom } from 'jotai';
import {
  LucideArchive,
  LucideHash,
  LucidePlusCircle,
  LucideRotateCcw,
  LucideType,
  LucideX,
} from 'lucide-react';

import { RoleActionConfirmationDialog } from '../_shared/RoleActionConfirmationDialog';

import { useExternalRolesQuery } from './_graphql/useExternalRolesQuery';
import { useExternalRoleUserCountsQuery } from './_graphql/useExternalRoleUserCountsQuery';
import { useCustomerPortalRoleActionDialog } from './_hooks/useCustomerPortalRoleActionDialog';
import { AddExternalRoleDialog } from './AddExternalRoleDialog';
import { ExternalRoleRecord } from './types';

import {
  RoleStatusBadge,
  SettingsContent,
} from '~/modules/settings/_components/_shared';
import { $openAddExternalRoleDialog } from '~/modules/settings/_state/addExternalRoleDialog.state';
import * as state from '~/modules/settings/_state/customerPortal.state';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import useDebouncedValue from '~/shared/hooks/useDebounce';

export const CustomerPortalRolesTabView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const openAddRoleDialog = useSetAtom($openAddExternalRoleDialog);

  const [selectedRoleIds, setSelectedRoleIds] = useAtom(
    state.$cpSelectedRoleIds,
  );
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [offset, setOffset] = useAtom(state.$cpRolesOffset);
  const [limit, setLimit] = useAtom(state.$cpRolesLimit);

  const roleActionDialog = useCustomerPortalRoleActionDialog({
    onSuccess: () => setSelectedRoleIds([]),
  });

  const {
    data: { roles: rolesData = [], total = 0 } = { roles: [], total: 0 },
    isLoading,
    error,
  } = useExternalRolesQuery({
    searchTerm: debouncedSearchTerm || undefined,
    sorting: [enumRoleSortOption.NameAsc],
    limit,
    offset,
  });

  const { data: userCountsData } = useExternalRoleUserCountsQuery({});

  const roles = React.useMemo(() => {
    const counts = userCountsData?.counts ?? {};
    return rolesData.map((role) => ({
      ...role,
      userCount: counts[role.id] ?? 0,
    }));
  }, [rolesData, userCountsData]);

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setOffset(0);
  };

  const handleRowClick = (role: ExternalRoleRecord) => {
    setLocation(`/customer-portal/roles/${role.id}` as never);
  };

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedRoleIds),
    [selectedRoleIds],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedRoleIds(Array.from(keys) as string[]);
    },
    [setSelectedRoleIds],
  );

  return (
    <>
      <SettingsContent className="@CustomerPortalRolesTabView h-full">
        <Col className="h-full gap-4">
          <Row gap2>
            <SearchField
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t.customerPortal.search.placeholder}
              autoFocus
            />
            <Button
              className="ml-auto"
              icon={<LucidePlusCircle />}
              onPress={openAddRoleDialog}
            >
              {t.customerPortal.actions.addRole}
            </Button>
          </Row>

          <TinyTable<ExternalRoleRecord>
            rowKey={(row) => row.id}
            selectable
            selectedKeys={selectedKeys}
            onSelectionChange={handleSelectionChange}
            isLoading={isLoading}
            error={error ?? undefined}
            onRowClick={handleRowClick}
            columns={[
              {
                key: 'name',
                label: t.customerPortal.roles.table.columns.name,
                icon: LucideType,
                cell: (row) => (
                  <span
                    className={row.status === 'archived' ? 'line-through' : ''}
                  >
                    {row.name}
                  </span>
                ),
              },
              {
                key: 'userCount',
                label: t.customerPortal.roles.table.columns.users,
                icon: LucideHash,
                width: 100,
                cell: (row) => row.userCount,
              },
              {
                key: 'status',
                label: t.customerPortal.roles.table.columns.status,
                width: 120,
                cell: (row) => <RoleStatusBadge status={row.status} />,
              },
              {
                key: 'actions',
                label: '',
                width: 60,
                preventRowClick: true,
                cell: (row) => (
                  <IconButton
                    icon={
                      row.status === 'archived' ? (
                        <LucideRotateCcw size={16} />
                      ) : (
                        <LucideArchive size={16} />
                      )
                    }
                    onPress={() => roleActionDialog.openForRole(row)}
                    isDisabled={roleActionDialog.isPending}
                    aria-label={
                      row.status === 'archived'
                        ? t.customerPortal.roles.table.actions.restoreRole
                        : t.customerPortal.roles.table.actions.archiveRole
                    }
                  />
                ),
              },
            ]}
            data={roles}
          />

          <FloatingToolbar visible={selectedRoleIds.length > 0}>
            <div className="flex flex-row items-center justify-between gap-3">
              <div className="flex flex-row items-center gap-2 text-sm">
                <Badge color="primary" size="small">
                  {selectedRoleIds.length}
                </Badge>{' '}
                {t.customerPortal.roles.bulk.selected}
              </div>
              <div className="flex flex-row items-center gap-2">
                <Button
                  variant="outline"
                  size="small"
                  icon={<LucideArchive className="size-4" />}
                  onPress={() =>
                    roleActionDialog.openBulkArchive(selectedRoleIds)
                  }
                  isDisabled={roleActionDialog.isPending}
                >
                  {t.customerPortal.roles.bulk.archive}
                </Button>
                <Button
                  variant="outline"
                  size="small"
                  icon={<LucideRotateCcw className="size-4" />}
                  onPress={() =>
                    roleActionDialog.openBulkRestore(selectedRoleIds)
                  }
                  isDisabled={roleActionDialog.isPending}
                >
                  {t.customerPortal.roles.bulk.restore}
                </Button>
                <LucideX
                  className="size-4 cursor-pointer text-neutral/80"
                  onClick={() => setSelectedRoleIds([])}
                />
              </div>
            </div>
          </FloatingToolbar>

          {total > 0 && (
            <Row className="justify-end">
              <Pagination
                limit={limit}
                offset={offset}
                total={total}
                onChange={handlePaginationChange}
              />
            </Row>
          )}
        </Col>
      </SettingsContent>

      <AddExternalRoleDialog />

      <RoleActionConfirmationDialog
        dialogState={roleActionDialog.dialogState}
        dialogs={t.customerPortal.roles.dialogs}
        isPending={roleActionDialog.isPending}
        onConfirm={roleActionDialog.confirm}
        onCancel={roleActionDialog.cancel}
        onOpenChange={roleActionDialog.handleOpenChange}
        className="@CustomerPortalRolesTabViewRoleActionDialog"
      />
    </>
  );
};
